import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import {
    CodeOutlined,
    DatabaseOutlined,
    CloudServerOutlined,
    RocketOutlined,
    NodeIndexOutlined,
    ApiOutlined,
    ToolOutlined,
    ClusterOutlined,
} from '@ant-design/icons';
import styles from './Skills.module.scss';
const cx = classNames.bind(styles);

function Skills() {
    const [isVisible, setIsVisible] = useState(false);
    const [shapesLoaded, setShapesLoaded] = useState(false);

    // Lấy trạng thái performance từ Redux store
    const highPerformance = useSelector((state) => state.performance.highPerformance);

    useEffect(() => {
        // Hiển thị nội dung ngay lập tức nếu không có hiệu ứng
        if (!highPerformance) {
            setShapesLoaded(true);
            setIsVisible(true);
            return;
        }

        // Xử lý animation với trình tự rõ ràng hơn khi có hiệu ứng
        let timers = [];

        // 1. Đầu tiên hiển thị background
        const shapesTimer = setTimeout(() => {
            setShapesLoaded(true);
        }, 100);
        timers.push(shapesTimer);

        // 2. Sau đó hiển thị nội dung sau khi background đã tải
        const contentTimer = setTimeout(() => {
            setIsVisible(true);
        }, 500);
        timers.push(contentTimer);

        // Xử lý khi tab được click
        const handleTabClick = () => {
            // Khi click tab, reset lại tất cả
            setIsVisible(false);
            setShapesLoaded(false);

            if (highPerformance) {
                // Dùng setTimeout để tạo hiệu ứng mượt mà khi có GPU
                setTimeout(() => {
                    setShapesLoaded(true);

                    // Đợi background xuất hiện rồi mới hiển thị nội dung
                    setTimeout(() => {
                        setIsVisible(true);
                    }, 300);
                }, 100);
            } else {
                // Hiển thị ngay lập tức khi không có hiệu ứng
                setShapesLoaded(true);
                setIsVisible(true);
            }
        };

        // Theo dõi sự kiện click vào tab
        const tabElement = document.querySelector('[href="/skill"]');
        if (tabElement) {
            tabElement.addEventListener('click', handleTabClick);
        }

        // Cleanup
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            if (tabElement) {
                tabElement.removeEventListener('click', handleTabClick);
            }
        };
    }, [highPerformance]); // Thêm dependency để useEffect chạy lại khi trạng thái performance thay đổi

    return (
        <div className={cx('skills-container', { 'no-animation': !highPerformance })}>
            {/* Background trang trí với class loaded để tạo hiệu ứng - chỉ hiển thị khi có hiệu ứng */}
            {highPerformance && (
                <div className={cx('decorative-background', { loaded: shapesLoaded })}>
                    {/* Giữ nguyên các hình cơ bản */}
                    <div className={cx('shape', 'square-1')}></div>
                    <div className={cx('shape', 'square-2')}></div>
                    <div className={cx('shape', 'square-3')}></div>
                    <div className={cx('shape', 'circle-1')}></div>
                    <div className={cx('shape', 'circle-2')}></div>
                    <div className={cx('shape', 'circle-3')}></div>
                    <div className={cx('shape', 'triangle-1')}></div>
                    <div className={cx('shape', 'triangle-2')}></div>
                    <div className={cx('shape', 'rectangle-1')}></div>
                    <div className={cx('shape', 'rectangle-2')}></div>
                    <div className={cx('shape', 'diamond-1')}></div>
                    <div className={cx('shape', 'diamond-2')}></div>
                    <div className={cx('shape', 'dot-group')}>
                        <div className={cx('dot')}></div>
                        <div className={cx('dot')}></div>
                        <div className={cx('dot')}></div>
                        <div className={cx('dot')}></div>
                    </div>
                    <div className={cx('shape', 'star-1')}></div>
                    <div className={cx('shape', 'hexagon-1')}></div>
                    <div className={cx('shape', 'wave-1')}></div>

                    {/* Thêm các hình trang trí phong phú hơn */}
                    <div className={cx('shape', 'zigzag')}></div>
                    <div className={cx('shape', 'blob-1')}></div>
                    <div className={cx('shape', 'blob-2')}></div>
                    <div className={cx('shape', 'donut')}></div>
                    <div className={cx('shape', 'half-circle')}></div>
                    <div className={cx('shape', 'cross')}></div>
                </div>
            )}

            <div
                className={cx('skills-content', {
                    'animate-content': isVisible && highPerformance,
                    'no-animation': !highPerformance,
                })}
            >
                {/* Frontend Development */}
                <div
                    className={cx('skills-section', 'frontend-card', {
                        animate: isVisible && highPerformance,
                        'delay-1': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <CodeOutlined className={cx('section-icon')} />
                        <h2>Frontend Development</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Ngôn ngữ & công nghệ</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    HTML
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>CSS</span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    SCSS
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    JavaScript
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Thư viện & Framework</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    ReactJS
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Redux Toolkit
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Ant Design
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Backend Development */}
                <div
                    className={cx('skills-section', 'backend-card', {
                        animate: isVisible && highPerformance,
                        'delay-2': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <ClusterOutlined className={cx('section-icon')} />
                        <h2>Backend Development</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Ngôn ngữ & Framework</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Java
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Spring Boot
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Spring AOP
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Spring Security
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Spring Cloud
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Netflix Eureka
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Spring WebFlux
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Xử lý bảo mật và xác thực</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    OAuth2
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Keycloak
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>JWT</span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>SSO</span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    OpenID Connect
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Zero Trust Architecture
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Database */}
                <div
                    className={cx('skills-section', 'database-card', {
                        animate: isVisible && highPerformance,
                        'delay-3': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <DatabaseOutlined className={cx('section-icon')} />
                        <h2>Cơ sở dữ liệu</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Quan hệ</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    MySQL
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    PostgreSQL
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Oracle
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Phi quan hệ</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    MongoDB
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Elasticsearch
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Redis
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Storage */}
                <div
                    className={cx('skills-section', 'storage-card', {
                        animate: isVisible && highPerformance,
                        'delay-4': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <CloudServerOutlined className={cx('section-icon')} />
                        <h2>Lưu trữ</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    MinIO
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Cloudinary
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* DevSecOps */}
                <div
                    className={cx('skills-section', 'devops-card', {
                        animate: isVisible && highPerformance,
                        'delay-5': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <RocketOutlined className={cx('section-icon')} />
                        <h2>DevSecOps</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>CI/CD & Quản lý pipeline</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    GitLab CI
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Jenkins
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Bảo mật và quét lỗ hổng</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Snyk
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Trivy
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Triển khai</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Docker
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Kubernetes
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Ubuntu
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Nginx
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Kiểm thử & Giám sát</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    K6 Test
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Zabbix
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Architecture */}
                <div
                    className={cx('skills-section', 'architecture-card', {
                        animate: isVisible && highPerformance,
                        'delay-6': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <NodeIndexOutlined className={cx('section-icon')} />
                        <h2>Kiến trúc hệ thống</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>MVC</span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>ECB</span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    SAGA
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Layered Architecture
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Microservices
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Domain-Driven Design
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Hexagonal
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    CQRS
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Other Technologies */}
                <div
                    className={cx('skills-section', 'other-tech-card', {
                        animate: isVisible && highPerformance,
                        'delay-7': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <ApiOutlined className={cx('section-icon')} />
                        <h2>Các công nghệ khác</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Giao tiếp thời gian thực</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    WebSocket
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Server-Sent Events
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Thông báo đẩy</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Firebase Cloud Messaging
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Xử lý đa luồng</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Multi-threading
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Xử lý dữ liệu lớn và logging</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Kafka
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Kibana
                                </span>
                            </div>
                        </div>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Ngôn ngữ khác</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Python
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>C</span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>C++</span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Kotlin
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    React Native Expo
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tools */}
                <div
                    className={cx('skills-section', 'tools-card', {
                        animate: isVisible && highPerformance,
                        'delay-8': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <ToolOutlined className={cx('section-icon')} />
                        <h2>Công cụ hỗ trợ</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    IntelliJ IDEA
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Postman
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    DataGrip
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    DBeaver
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Visual Studio Code
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Docker Desktop
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    VMware
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Development Process */}
                <div
                    className={cx('skills-section', 'process-card', {
                        animate: isVisible && highPerformance,
                        'delay-9': isVisible && highPerformance,
                        'no-animation': !highPerformance,
                        'no-hover': !highPerformance,
                    })}
                >
                    <div className={cx('section-header')}>
                        <ClusterOutlined className={cx('section-icon')} />
                        <h2>Quy trình & mô hình phát triển</h2>
                    </div>
                    <div className={cx('skills-group')}>
                        <div className={cx('skill-category')}>
                            <div className={cx('category-title-wrapper')}>
                                <div className={cx('category-title')}>Quy trình phát triển</div>
                            </div>
                            <div className={cx('skill-tags')}>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>
                                    Git Flow
                                </span>
                                <span className={cx('skill-tag', { 'no-hover-animation': !highPerformance })}>UML</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default React.memo(Skills);
